export const HEX_COLOR_REGEX = /^#[0-9A-F]{6}$/i;

export const normalizeHex = (input: string): string => {
  return input.trim().toUpperCase();
};

export const isValidHex = (input: string): boolean => {
  return HEX_COLOR_REGEX.test(input);
};

// OKLCH lightness and chroma of the colors from colorFromId: dark enough for a 2px line on a white
// surface (>= 3:1), the same for every hue so no color stands out.
const ID_COLOR_LIGHTNESS = 0.6;
const ID_COLOR_CHROMA = 0.14;

const toHexChannel = (linear: number) => {
  const value = linear <= 0.0031308 ? 12.92 * linear : 1.055 * Math.pow(linear, 1 / 2.4) - 0.055;
  return Math.round(Math.min(1, Math.max(0, value)) * 255)
    .toString(16)
    .padStart(2, "0");
};

// The linear sRGB channels of an OKLCH color (https://bottosson.github.io/posts/oklab/).
const oklchToLinearSrgb = (lightness: number, chroma: number, hue: number): [number, number, number] => {
  const a = chroma * Math.cos((hue * Math.PI) / 180);
  const b = chroma * Math.sin((hue * Math.PI) / 180);
  const l = (lightness + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (lightness - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
};

const isInSrgbGamut = (channels: number[]) => channels.every((channel) => channel >= -1e-4 && channel <= 1 + 1e-4);

/**
 * A color for an id, e.g. of an app or a module in a chart: always the same for the same id, and one
 * for any new id. The hue comes from a hash of the id; lightness and chroma are fixed (the chroma is
 * lowered only for hues sRGB can't show that saturated). Two ids can get close hues.
 */
export const colorFromId = (id: string): string => {
  // FNV-1a
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash = Math.imul(hash ^ id.charCodeAt(i), 0x01000193);
  }
  const hue = (hash >>> 0) % 360;

  let chroma = ID_COLOR_CHROMA;
  while (chroma > 0 && !isInSrgbGamut(oklchToLinearSrgb(ID_COLOR_LIGHTNESS, chroma, hue))) {
    chroma -= 0.005;
  }

  return `#${oklchToLinearSrgb(ID_COLOR_LIGHTNESS, Math.max(0, chroma), hue).map(toHexChannel).join("")}`;
};
